import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProfileAddonsRequest } from "Api/billingSystem/profile/getProfileAddons.request";
import { updateProfileAddonsRequest } from "Api/billingSystem/profile/updateProfileAddons.request";

import type { RootState } from "Store/configureStore";
import type {
  BillingProfileAddons,
  BillingProfileAddonsUpdatePayload
} from "Api/billingSystem/types";

// Thunks
export const getProfileAddonsThunk = createAppAsyncThunk(
  "billingSystem/profileAddons/get",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getProfileAddonsRequest(billingProfileId);
    } catch (err) {
      logger(err, { action: "get_profile_addons", billingProfileId });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const updateProfileAddonsThunk = createAppAsyncThunk(
  "billingSystem/profileAddons/update",
  async (
    {
      billingProfileId,
      payload
    }: {
      billingProfileId: string;
      payload: BillingProfileAddonsUpdatePayload;
    },
    { rejectWithValue }
  ) => {
    try {
      return await updateProfileAddonsRequest(billingProfileId, payload);
    } catch (err) {
      logger(err, { action: "update_profile_addons", billingProfileId });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type ProfileAddonsState = {
  dataByProfileId: Record<
    string,
    {
      addons?: BillingProfileAddons;
      loaded: boolean;
      loading: boolean;
      error?: string;
    }
  >;
  updateState: Record<
    string,
    {
      loading: boolean;
      error?: string;
    }
  >;
};

const initialState: ProfileAddonsState = {
  dataByProfileId: {},
  updateState: {}
};

// Slice
const profileAddonsSlice = createSlice({
  name: "profileAddons",
  initialState,
  reducers: {},
  extraReducers: builder => {
    // GET
    builder
      .addCase(getProfileAddonsThunk.pending, (state, { meta }) => {
        const { billingProfileId } = meta.arg;
        setDeep(state, ["dataByProfileId", billingProfileId, "loading"], true);
        setDeep(
          state,
          ["dataByProfileId", billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getProfileAddonsThunk.fulfilled, (state, { meta, payload }) => {
        const { billingProfileId } = meta.arg;
        setDeep(state, ["dataByProfileId", billingProfileId, "loading"], false);
        setDeep(state, ["dataByProfileId", billingProfileId, "loaded"], true);
        setDeep(
          state,
          ["dataByProfileId", billingProfileId, "addons"],
          payload
        );
      })
      .addCase(getProfileAddonsThunk.rejected, (state, { meta, payload }) => {
        const { billingProfileId } = meta.arg;
        setDeep(state, ["dataByProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["dataByProfileId", billingProfileId, "error"],
          payload?.error
        );
      });

    // PATCH
    builder
      .addCase(updateProfileAddonsThunk.pending, (state, { meta }) => {
        const { billingProfileId } = meta.arg;
        setDeep(state, ["updateState", billingProfileId, "loading"], true);
        setDeep(state, ["updateState", billingProfileId, "error"], undefined);
      })
      .addCase(
        updateProfileAddonsThunk.fulfilled,
        (state, { meta, payload }) => {
          const { billingProfileId } = meta.arg;
          setDeep(state, ["updateState", billingProfileId, "loading"], false);
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "addons"],
            payload
          );
        }
      )
      .addCase(
        updateProfileAddonsThunk.rejected,
        (state, { meta, payload }) => {
          const { billingProfileId } = meta.arg;
          setDeep(state, ["updateState", billingProfileId, "loading"], false);
          setDeep(
            state,
            ["updateState", billingProfileId, "error"],
            payload?.error
          );
        }
      );
  }
});

export default profileAddonsSlice.reducer;

// Selectors
const profileAddonsStateSelector = (state: RootState) => state.profileAddons;

export const selectProfileAddons = createSelector(
  profileAddonsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.dataByProfileId[billingProfileId]?.addons
);

export const selectProfileAddonsLoaded = createSelector(
  profileAddonsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.dataByProfileId[billingProfileId]?.loaded ?? false
);

export const selectProfileAddonsLoading = createSelector(
  profileAddonsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.dataByProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileAddonsError = createSelector(
  profileAddonsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.dataByProfileId[billingProfileId]?.error
);

export const selectProfileAddonsUpdateLoading = createSelector(
  profileAddonsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.updateState[billingProfileId]?.loading ?? false
);

export const selectProfileAddonsUpdateError = createSelector(
  profileAddonsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.updateState[billingProfileId]?.error
);
